<template>
  <v-app class="admin-shell">
    <vertical-nav-menu
      :is-mobile="isMobile"
      :is-drawer-collapsed="isDrawerCollapsed"
      :is-mobile-drawer-open="isMobileDrawerOpen"
      @update:is-mobile-drawer-open="isMobileDrawerOpen = $event"
    ></vertical-nav-menu>
    <v-app-bar
      app
      flat
      color="transparent"
      height="56"
      class="admin-topbar"
    >
      <div class="admin-shell__container">
        <div class="admin-topbar__inner">
          <div class="admin-topbar__left">
            <v-btn
              icon
              class="admin-topbar__toggle"
              aria-label="Menüyü aç veya kapat"
              title="Menüyü aç veya kapat"
              @click="toggleDrawer"
            >
              <v-icon>
                {{ drawerToggleIcon }}
              </v-icon>
            </v-btn>

            <span class="admin-topbar__title">{{ $route.meta.title || 'Dashboard' }}</span>
          </div>

          <div class="admin-topbar__actions">
            <theme-switcher></theme-switcher>
            <v-badge
              :value="notificationBadgeCount > 0"
              :content="notificationBadgeCount"
              color="primary"
              overlap
            >
              <v-btn
                icon
                class="admin-icon-button"
                aria-label="Bildirimler"
                title="Bildirimler"
                :disabled="!canOpenNotifications"
                @click="openNotifications"
              >
                <v-icon>{{ icons.mdiBellOutline }}</v-icon>
              </v-btn>
            </v-badge>
            <app-bar-user-menu></app-bar-user-menu>
          </div>
        </div>
      </div>
    </v-app-bar>
    <v-main>
      <div class="app-content-container pa-6 admin-page-container">
        <slot></slot>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import axios from 'axios'
import {
  mdiBellOutline,
  mdiChevronLeft,
  mdiChevronRight,
  mdiClose,
  mdiMenu,
} from '@mdi/js'
import VerticalNavMenu from './components/vertical-nav-menu/VerticalNavMenu.vue'
import ThemeSwitcher from './components/ThemeSwitcher.vue'
import AppBarUserMenu from './components/AppBarUserMenu.vue'
import {
  clearPanelSession,
  getAccessToken,
  getPanelSession,
  hasAdminAccess,
  hasPanelAccess,
} from '@/utils/panelSession'

export default {
  components: {
    VerticalNavMenu,
    ThemeSwitcher,
    AppBarUserMenu,
  },
  data() {
    return {
      icons: {
        mdiBellOutline,
        mdiChevronLeft,
        mdiChevronRight,
        mdiClose,
        mdiMenu,
      },
      isDrawerCollapsed: false,
      isMobileDrawerOpen: false,
      notificationBadgeCount: 0,
      notificationPollTimer: null,
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown
    },
    drawerToggleIcon() {
      if (this.isMobile) {
        return this.isMobileDrawerOpen ? this.icons.mdiClose : this.icons.mdiMenu
      }

      return this.isDrawerCollapsed ? this.icons.mdiChevronRight : this.icons.mdiChevronLeft
    },
    canOpenNotifications() {
      return hasAdminAccess(getPanelSession())
    },
  },
  watch: {
    $route() {
      this.enforcePanelAuth()
    },
  },
  mounted() {
    this.enforcePanelAuth()
    this.refreshPanelNotifications()
    this.notificationPollTimer = window.setInterval(this.refreshPanelNotifications, 30000)

    window.addEventListener('focus', this.enforcePanelAuth)
    window.addEventListener('storage', this.enforcePanelAuth)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    if (this.notificationPollTimer) {
      window.clearInterval(this.notificationPollTimer)
    }
    window.removeEventListener('focus', this.enforcePanelAuth)
    window.removeEventListener('storage', this.enforcePanelAuth)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    handleVisibilityChange() {
      if (!document.hidden) {
        this.enforcePanelAuth()
        this.refreshPanelNotifications()
      }
    },
    enforcePanelAuth() {
      const currentRoute = this.$route

      if (!currentRoute || currentRoute.meta?.layout === 'blank' || currentRoute.name === 'login') {
        return
      }

      const hasToken = !!getAccessToken()
      const hasSession = hasPanelAccess(getPanelSession())

      if (hasToken && hasSession) {
        return
      }

      clearPanelSession()
      this.$router.replace({ name: 'login', query: { reset: '1' } }).catch(() => {})
    },
    toggleDrawer() {
      if (this.isMobile) {
        this.isMobileDrawerOpen = !this.isMobileDrawerOpen

        return
      }

      this.isDrawerCollapsed = !this.isDrawerCollapsed
    },
    openNotifications() {
      if (!this.canOpenNotifications || this.$route.name === 'broadcastNotifications') return

      this.notificationBadgeCount = 0
      this.$router.push({ name: 'broadcastNotifications' })
    },
    async refreshPanelNotifications() {
      if (!this.canOpenNotifications || !getAccessToken()) return

      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/notifications/panel-feed`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        })
        const items = Array.isArray(response?.data?.data) ? response.data.data : []
        if (!items.length) return

        const latestId = Math.max(...items.map(item => Number(item.id || 0)))
        const storageKey = 'panelNotificationLatestId'
        const previousId = Number(localStorage.getItem(storageKey) || 0)

        if (!previousId) {
          localStorage.setItem(storageKey, String(latestId))

          return
        }

        if (latestId > previousId) {
          this.notificationBadgeCount += items.filter(item => Number(item.id || 0) > previousId).length
          localStorage.setItem(storageKey, String(latestId))
        }
      } catch (error) {
        // Notification polling should not interrupt panel navigation.
      }
    },
  },
}
</script>
